import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import StarRating from './StarRating';
import StateBlock from './StateBlock';
import { apiUrl, mediaUrl } from '../../config/api';
import './ReviewShowcase.scss';

/**
 * Customer review gallery, shared by the landing-page rail
 * (`/api/fetch-best-reviews`) and the full page
 * (`/api/all-fetch-best-reviews`), which were two copies of the same
 * component differing only in that endpoint.
 */
const ReviewShowcase = ({
  endpoint,
  layout = 'rail',
  // Pins the grid to a fixed column count instead of auto-filling.
  columns,
  // Caps how many reviews render, for the landing-page section.
  limit,
  skeletonCount = 4,
}) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReviews = useCallback(
    async (signal) => {
      setLoading(true);
      try {
        const response = await fetch(apiUrl(endpoint), { signal });
        if (!response.ok) throw new Error('Failed to fetch reviews');
        const data = await response.json();
        setReviews(Array.isArray(data?.reviews) ? data.reviews : []);
        setError(null);
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [endpoint]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchReviews(controller.signal);
    return () => controller.abort();
  }, [fetchReviews]);

  const containerClass =
    layout === 'rail'
      ? 'review-rail'
      : `review-showcase-grid${columns ? ` review-showcase-grid--${columns}up` : ''}`;

  const visibleReviews = limit ? reviews.slice(0, limit) : reviews;

  if (loading) {
    return (
      <div className={containerClass} aria-busy="true">
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <div className="review-tile" key={index} aria-hidden="true">
            <div className="skeleton review-tile__media" />
            <div className="review-tile__body">
              <div className="skeleton skeleton--text" style={{ width: '40%' }} />
              <div className="skeleton skeleton--text" />
              <div className="skeleton skeleton--text" style={{ width: '70%' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <StateBlock
        variant="error"
        title="Reviews are unavailable"
        message="We couldn't load customer reviews right now."
        actionLabel="Retry"
        onAction={() => fetchReviews()}
      />
    );
  }

  if (visibleReviews.length === 0) {
    return (
      <StateBlock
        title="No reviews yet"
        message="Once customers start sharing their photos, they will appear here."
      />
    );
  }

  return (
    <div className={containerClass}>
      {visibleReviews.map((review) => {
        // Prefer the customer's own photo; fall back to the product shot.
        const image =
          mediaUrl(review.review_image_1) || mediaUrl(review?.product?.product_image_1);
        const productCode = review?.product?.product_code;

        return (
          <article className="review-tile" key={review.id}>
            {image ? (
              <img className="review-tile__media" src={image} alt="" loading="lazy" decoding="async" />
            ) : (
              <span className="review-tile__media" aria-hidden="true" />
            )}

            <div className="review-tile__body">
              <StarRating value={review.rating} size="sm" />
              <p className="review-tile__text">{review.review}</p>
              {review.user_name && <p className="review-tile__author">— {review.user_name}</p>}

              {productCode && (
                <Link className="btn btn--outline btn--sm btn--block" to={`/productshow/${productCode}`}>
                  Shop this piece
                </Link>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default ReviewShowcase;
